import { useCallback, useEffect, useRef, useState } from "react";
import {
  usePatchSaveDoc,
  type UsePatchSaveDocOptions,
} from "@/lib/hooks/usePatchSaveDoc";
import { type FieldSaveState, IDLE_FIELD_STATE } from "./types";

export interface UseInlineFieldSaveOptions extends UsePatchSaveDocOptions {}

export interface UseInlineFieldSaveReturn {
  /** Save a single field value. Returns true on success. */
  saveField: (
    fieldname: string,
    value: unknown,
    previousValue?: unknown
  ) => Promise<boolean>;
  /** Get the current save state for a field. */
  getFieldState: (fieldname: string) => FieldSaveState;
  /** Clear error state for a field (resets to idle). */
  clearError: (fieldname: string) => void;
  /** Undo last save for a field — restores and saves previous value. */
  undoField: (fieldname: string) => Promise<boolean>;
  /** Retry a failed save for a field. */
  retryField: (fieldname: string) => Promise<boolean>;
  /** Check if a field has an undoable save within the 5s window. */
  getUndoable: (fieldname: string) => boolean;
  /** The fieldname of the last successfully saved field (for undo hook). */
  lastSavedField: string | null;
}

/** Undo window duration (ms) */
const UNDO_WINDOW_MS = 5000;
/** Saved indicator display duration (ms) */
const SAVED_INDICATOR_MS = 1500;

/**
 * Per-field save orchestration wrapping usePatchSaveDoc.
 *
 * - One hook instance per detail page (shared across all inline fields)
 * - Tracks saving/saved/error state per fieldname
 * - Auto-clears "saved" indicator after 1.5s
 * - Supports undo (previous value tracking with 5s window)
 * - Supports retry (stores failed value)
 * - Rollback handled by usePatchSaveDoc's onError
 */
export function useInlineFieldSave(
  opts: UseInlineFieldSaveOptions
): UseInlineFieldSaveReturn {
  const mutation = usePatchSaveDoc(opts);

  // Map<fieldname, FieldSaveState> — mutable ref, re-render triggered by counter
  const stateMapRef = useRef(new Map<string, FieldSaveState>());
  // Timer map for auto-clearing "saved" status
  const timerMapRef = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  // Previous values for undo — auto-cleared after UNDO_WINDOW_MS
  const previousValueMapRef = useRef(
    new Map<string, { value: unknown; savedAt: number }>()
  );
  // Undo cleanup timers
  const undoTimerMapRef = useRef(
    new Map<string, ReturnType<typeof setTimeout>>()
  );
  // Failed values for retry
  const failedValueMapRef = useRef(new Map<string, unknown>());
  // Counter to trigger re-renders when state map changes
  const [, setRenderTick] = useState(0);
  // Last successfully saved field
  const [lastSavedField, setLastSavedField] = useState<string | null>(null);

  // Cleanup all timers on unmount
  useEffect(() => {
    return () => {
      timerMapRef.current.forEach((timer) => clearTimeout(timer));
      timerMapRef.current.clear();
      undoTimerMapRef.current.forEach((timer) => clearTimeout(timer));
      undoTimerMapRef.current.clear();
    };
  }, []);

  const updateFieldState = useCallback(
    (fieldname: string, state: FieldSaveState) => {
      stateMapRef.current.set(fieldname, state);
      setRenderTick((c) => c + 1);
    },
    []
  );

  const saveField = useCallback(
    async (
      fieldname: string,
      value: unknown,
      previousValue?: unknown
    ): Promise<boolean> => {
      // Clear any existing auto-clear timer for this field
      const existingTimer = timerMapRef.current.get(fieldname);
      if (existingTimer) clearTimeout(existingTimer);

      // Store previous value for undo (only if explicitly provided)
      if (previousValue !== undefined) {
        // Clear existing undo timer for this field
        const existingUndoTimer = undoTimerMapRef.current.get(fieldname);
        if (existingUndoTimer) clearTimeout(existingUndoTimer);

        previousValueMapRef.current.set(fieldname, {
          value: previousValue,
          savedAt: Date.now(),
        });

        // Auto-clear undo data after 5s
        const undoTimer = setTimeout(() => {
          previousValueMapRef.current.delete(fieldname);
          undoTimerMapRef.current.delete(fieldname);
        }, UNDO_WINDOW_MS);
        undoTimerMapRef.current.set(fieldname, undoTimer);
      }

      // Clear any previous failed value for this field
      failedValueMapRef.current.delete(fieldname);

      updateFieldState(fieldname, { status: "saving" });

      try {
        await mutation.mutateAsync({ [fieldname]: value });

        const now = Date.now();
        updateFieldState(fieldname, { status: "saved", lastSavedAt: now });
        setLastSavedField(fieldname);

        // Auto-clear saved indicator after 1.5s
        const timer = setTimeout(() => {
          const current = stateMapRef.current.get(fieldname);
          if (current?.status === "saved" && current.lastSavedAt === now) {
            stateMapRef.current.set(fieldname, IDLE_FIELD_STATE);
            setRenderTick((c) => c + 1);
          }
        }, SAVED_INDICATOR_MS);
        timerMapRef.current.set(fieldname, timer);

        return true;
      } catch (err: unknown) {
        const message =
          err instanceof Error
            ? err.message
            : typeof err === "object" && err !== null && "exc_type" in err
              ? String((err as Record<string, unknown>).exc_type)
              : "Save failed";

        // Store failed value for retry
        failedValueMapRef.current.set(fieldname, value);
        updateFieldState(fieldname, {
          status: "error",
          error: message,
          failedValue: value,
        });
        return false;
      }
    },
    [mutation, updateFieldState]
  );

  const undoField = useCallback(
    async (fieldname: string): Promise<boolean> => {
      const entry = previousValueMapRef.current.get(fieldname);
      if (!entry) return false;

      // Clear undo data — no undo chain (don't pass previousValue)
      previousValueMapRef.current.delete(fieldname);
      const undoTimer = undoTimerMapRef.current.get(fieldname);
      if (undoTimer) {
        clearTimeout(undoTimer);
        undoTimerMapRef.current.delete(fieldname);
      }

      // Save the previous value WITHOUT previousValue param (prevents undo chain)
      return saveField(fieldname, entry.value);
    },
    [saveField]
  );

  const retryField = useCallback(
    async (fieldname: string): Promise<boolean> => {
      const failedValue = failedValueMapRef.current.get(fieldname);
      if (failedValue === undefined) return false;

      failedValueMapRef.current.delete(fieldname);
      return saveField(fieldname, failedValue);
    },
    [saveField]
  );

  const getUndoable = useCallback((fieldname: string): boolean => {
    return previousValueMapRef.current.has(fieldname);
  }, []);

  const getFieldState = useCallback(
    (fieldname: string): FieldSaveState => {
      return stateMapRef.current.get(fieldname) ?? IDLE_FIELD_STATE;
    },
    []
  );

  const clearError = useCallback(
    (fieldname: string) => {
      const current = stateMapRef.current.get(fieldname);
      if (current?.status === "error") {
        failedValueMapRef.current.delete(fieldname);
        updateFieldState(fieldname, IDLE_FIELD_STATE);
      }
    },
    [updateFieldState]
  );

  return {
    saveField,
    getFieldState,
    clearError,
    undoField,
    retryField,
    getUndoable,
    lastSavedField,
  };
}
